import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'ith_preferred_currency';

/**
 * useCurrency hook — manages user's preferred display currency.
 *
 * The preferred currency is stored in localStorage.
 * The hook also provides a `convert()` function to convert prices server-side.
 *
 * Usage:
 *   const { currency, setCurrency, convert, isLoading } = useCurrency();
 *   const { convertedAmount } = await convert(100, 'EUR'); // returns in user's currency
 */
const useCurrency = () => {
  const [currency, setCurrencyState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || process.env.REACT_APP_DEFAULT_CURRENCY || 'EUR';
    } catch (_) {
      return 'EUR';
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  const setCurrency = useCallback(code => {
    setCurrencyState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (_) {}
  }, []);

  const convert = useCallback(async (amount, from) => {
    if (from === currency) return { convertedAmount: amount, formatted: `${currency} ${amount}` };
    setIsLoading(true);
    try {
      const res = await fetch('/api/currency/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, from, to: currency }),
      });
      if (!res.ok) throw new Error('Conversion failed');
      return await res.json();
    } finally {
      setIsLoading(false);
    }
  }, [currency]);

  return { currency, setCurrency, convert, isLoading };
};

export default useCurrency;
